import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup
} from "react-leaflet";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "recharts";

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { baseUrl } from "../Univ.js";

/* ============================
   FIX LEAFLET ICON ISSUE
============================ */
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

/* ========================= */

const OVERHEAT_TEMP = 50;

function ControlRoomDashboard() {

  const [devices, setDevices] = useState({});
  const [history, setHistory] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [routes, setRoutes] = useState({});
  const fetchInitialData = () => {
    axios.get(baseUrl + "iot-data/")
      .then(res => {

        const latest = {};
        const routeData = {};

        res.data.forEach(item => {

          if (item.latitude && item.longitude) {

            const devId = item.device.device_id;

            latest[devId] = {
              device_id: devId,
              latitude: item.latitude,
              longitude: item.longitude,
              temperature: item.temperature,
              humidity: item.humidity
            };

            if (!routeData[devId]) {
              routeData[devId] = [];
            }

            routeData[devId].push([
              item.latitude,
              item.longitude
            ]);
          }
        });

        setDevices(Object.values(latest));
        setRoutes(routeData);
      });
  };

  /* ============================
        WEBSOCKET
  ============================ */
  useEffect(() => {

  fetchInitialData();

    const socket = new WebSocket("ws://localhost:8000/ws/iot/");

    socket.onmessage = (event) => {

      const liveData = JSON.parse(event.data);

      // ✅ normalize device id
      const deviceId =
        liveData.device || liveData.device_id;

      const timestamp = new Date().toLocaleTimeString();

      console.log("LIVE DATA:", liveData);

      /* ---------- DEVICES ---------- */
      setDevices(prev => ({
        ...prev,
        [deviceId]: {
          device_id: deviceId,
          temperature: liveData.temperature,
          humidity: liveData.humidity,
          latitude: liveData.latitude,
          longitude: liveData.longitude
        }
      }));

      /* ---------- HISTORY ---------- */
      setHistory(prev => ({
        ...prev,
        [deviceId]: [
          ...(prev[deviceId] || []).slice(-20),
          {
            time: timestamp,
            temperature: liveData.temperature,
            humidity: liveData.humidity
          }
        ]
      }));

      /* ---------- ALERTS ---------- */
      if (liveData.temperature > OVERHEAT_TEMP) {
        setAlerts(prev => [
          {
            device: deviceId,
            message: "🔥 OVERHEAT DETECTED",
            time: timestamp
          },
          ...prev.slice(0, 20)
        ]);
      }
    };

    return () => socket.close();

  }, []);

  /* ============================
        UI
  ============================ */
  return (
    <div style={{
      background: "#111",
      color: "#fff",
      minHeight: "100vh",
      padding: "20px"
    }}>

      <h2 style={{ textAlign: "center" }}>
        🏭 PRODUCTION IoT CONTROL ROOM
      </h2>

      {/* ============================
            DEVICE CARDS
      ============================ */}
      <div style={{
        display: "flex",
        gap: "20px",
        flexWrap: "wrap"
      }}>

        {Object.keys(devices).map(deviceId => {

          const device = devices[deviceId];
          const isHot = device.temperature > OVERHEAT_TEMP;

          return (
            <div key={deviceId}
              style={{
                background: isHot ? "#5c0000" : "#1b1b1b",
                padding: "15px",
                borderRadius: "10px",
                width: "250px",
                boxShadow: "0 0 10px #000"
              }}
            >
              <h5>{deviceId}</h5>
              <p>🌡 {device.temperature} °C</p>
              <p>💧 {device.humidity} %</p>
              <p>Status: {isHot ? "🔥 CRITICAL" : "🟢 NORMAL"}</p>
            </div>
          );
        })}
      </div>

      {/* ============================
            CHARTS
      ============================ */}
      <div style={{ marginTop: "40px" }}>

        {Object.keys(history).map(deviceId => (

          <div key={deviceId} style={{ marginBottom: "40px" }}>
            <h5>{deviceId} Temperature</h5>

            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={history[deviceId]}>
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="temperature"
                  stroke="#ff4444"
                />
              </LineChart>
            </ResponsiveContainer>

          </div>
        ))}
      </div>

      {/* ============================
            MAP
      ============================ */}
      <div style={{ marginTop: "40px" }}>
        <h4>📍 LIVE GPS MAP</h4>

        <MapContainer
          center={[20.5937, 78.9629]}
          zoom={5}
          style={{ height: "400px", width: "100%" }}
        >

          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {Object.keys(devices).map(deviceId => {

            const device = devices[deviceId];

            if (!device.latitude || !device.longitude)
              return null;

            return (
              <Marker
                key={deviceId}
                position={[
                  device.latitude,
                  device.longitude
                ]}
              >
                <Popup>
                  <strong>{deviceId}</strong><br />
                  🌡 {device.temperature} °C<br />
                  💧 {device.humidity} %
                </Popup>
              </Marker>
            );
          })}

        </MapContainer>
      </div>

      {/* ============================
            ALERT PANEL
      ============================ */}
      <div style={{ marginTop: "40px" }}>
        <h4>🚨 ALERTS</h4>

        <div style={{
          background: "#1b1b1b",
          padding: "15px",
          borderRadius: "10px",
          maxHeight: "200px",
          overflowY: "auto"
        }}>

          {alerts.length === 0 && <p>No Alerts</p>}

          {alerts.map((alert, index) => (
            <div key={index}
              style={{
                borderBottom: "1px solid #333",
                padding: "5px"
              }}
            >
              <strong>{alert.device}</strong> — {alert.message} ({alert.time})
            </div>
          ))}

        </div>
      </div>

    </div>
  );
}

export default ControlRoomDashboard;
